// #19 Segmented: a radiogroup of two to four choices (*Board · Table*, *virtual · onsite · hybrid*, *Internal ·
// External*, *Not Required · Required*). One tab stop on the chosen item (roving tabindex); the arrows move and choose,
// with wrap; Home/End jump. Blocked (DC-24, DS "D values only"): the choices stay visible with the chosen one marked,
// the group and every item are aria-disabled and described by the inline reason, nothing changes; mode="view" draws
// the chosen words alone. Named by `label` (aria-label) or `aria-labelledby` (a lane field's or a Field's label).

import type { ReactElement } from "react";
import { useBlocked, type ControlProps } from "../core/Button";
import { Icon, type IconName } from "../core/icons";
import { cx } from "../lib/cx";
import { useHs, type Hs } from "../lib/hs";
import { Tooltip } from "../lib/Tooltip";
import { useRoving } from "../lib/useRoving";

export interface SegmentedOption<V extends string = string> {
  value: V;
  /** The choice's words, from a labels module. */
  label: string;
  icon?: IconName | undefined;
  hs?: Hs | undefined;
}

export interface SegmentedProps<V extends string = string> extends Omit<ControlProps, "hs"> {
  options: readonly SegmentedOption<V>[];
  value: V;
  onChange?: ((value: V) => void) | undefined;
  /** The group's name (aria-label), when no visible label names it. */
  label?: string | undefined;
  "aria-labelledby"?: string | undefined;
  /** The lane's full-width form: each choice takes an equal share. */
  fill?: boolean | undefined;
  id?: string | undefined;
  className?: string | undefined;
}

function Choice({ option, on, inert, describedBy, item, onPick }: {
  option: SegmentedOption;
  on: boolean;
  inert: boolean;
  describedBy: string | undefined;
  item: ReturnType<ReturnType<typeof useRoving>["item"]>;
  onPick: () => void;
}) {
  const hot = useHs(option.hs);
  return (
    <button
      type="button"
      role="radio"
      aria-checked={on}
      aria-disabled={inert ? true : undefined}
      aria-describedby={describedBy}
      className={cx("mc-sg", on && "mc-on")}
      {...item}
      {...hot}
      onClick={() => { if (!inert) onPick(); }}
    >
      {option.icon ? <Icon name={option.icon} size={14} /> : null}
      {option.label}
    </button>
  );
}

export function Segmented<V extends string = string>({
  options, value, onChange, label, "aria-labelledby": labelledBy, fill, can, blocked, mode, id, className,
}: SegmentedProps<V>) {
  const chosen = options.findIndex((option) => option.value === value);
  const inert = Boolean(blocked);
  const roving = useRoving({
    count: options.length,
    active: chosen,
    orientation: "horizontal",
    selectOnFocus: !inert,
    onSelect: (index) => { const next = options[index]; if (next) onChange?.(next.value); },
  });
  const block = useBlocked(blocked);
  if (can === false) return null;
  if (mode === "view") return <span className="mc-seg-v">{options[chosen]?.label ?? "—"}</span>;
  const group: ReactElement = (
    <span
      id={id}
      role="radiogroup"
      aria-label={labelledBy ? undefined : label}
      aria-labelledby={labelledBy}
      aria-disabled={inert ? true : undefined}
      className={cx("mc-seg", fill && "mc-seg-fill", inert && "mc-dis", className)}
      {...roving.container}
    >
      {options.map((option, index) => (
        <Choice
          key={option.value}
          option={option}
          on={index === chosen}
          inert={inert}
          describedBy={block.describedBy}
          item={roving.item(index)}
          onPick={() => { roving.focus(index); if (index !== chosen) onChange?.(option.value); }}
        />
      ))}
    </span>
  );
  if (!blocked) return group;
  return (
    <>
      <Tooltip label={blocked.reason} mode="mirror">{group}</Tooltip>
      {block.reason}
    </>
  );
}
